import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import FileUploadIcon from '@mui/icons-material/FileUpload';
import ReplayIcon from "@mui/icons-material/Replay";
import InfoIcon from "@mui/icons-material/Info";
import { Card, CardHeader, CardContent, Grid, TextField, FormHelperText, 
  IconButton, Tooltip, Box, List, Stack, SxProps, Theme, Typography, FormControl, 
  FormControlLabel, FormLabel, ListItem, Radio, RadioGroup, CircularProgress, 
  Checkbox, InputAdornment, Alert, Button, Backdrop, FormGroup, Switch } from "@mui/material";
import { FC, useEffect, useRef, useState } from "react";
import * as Yup from 'yup';
import { AssembleIssue, SubdataAssembleContextProvider, useSubdataAssembleContext, useSubdataAssembleController } from "../input/useSubdataAssembleController";
import { DocumentTemplateResponseDto, DocumentTemplateInput, DocumentKeywordRule, ExtractDocumentResponseDto } from "~/gen/api/securefile";
import { crudGetList, useRecordContext } from "ra-core";
import { usePlainStateFormUtils } from "../input/usePlainStateFormUtils";
import { promiseWithResolvers } from "~/utils/promiseWithResolvers";
import StringArrayTextField from "../input/StringArrayTextField";
import { waitWith } from "~/utils/waitWith";
import { DropEvent, useDropzone } from "react-dropzone";
import CreateOrEdit from "~/controller/CreateOrEdit";
import WithSave from "~/controller/WithSave";
import { useDefaultSavedHandlers } from "~/controller/defaultSavedHandlers";
import { useSnackbar } from "notistack";
import { useDispatch } from "react-redux";
import { merge } from "lodash";
import { pages, sorts } from "~/utils/paramsWithGetMainList";
import retrieveAxiosErr from "~/utils/retrieveAxiosErr";

const topSchema = Yup.object({
  name: Yup.string().required('Name is required').max(100, 'Name must be at most 100 characters'),
  displayName: Yup.string().required('Display Name is required').max(100, 'Display Name must be at most 100 characters'),
  description: Yup.string().max(512, 'Description must be at most 512 characters'),
  template: Yup.string().required('Template File needs to be uploaded first'),
})

const DocumentTypeEditor: FC<{ title: string}> = ({ title }) => {
  const record = useRecordContext<DocumentTemplateResponseDto>() ?? emptyDocTypeDto
  // const { data: baseInfoTypeData, } = useGetMainList(baseInfoTypeRes, pages(), sorts(), {}, { action: 'CUSTOM_DATA_QUERY' })
  const createMode = record === emptyDocTypeDto


  const initialVal = (record: DocumentTemplateResponseDto) => ({ 
    prevData: record,
    stage: {
      recordId: record.id,
      name: record.name,
      displayName: record.displayName,
      description: record.description,
      template: record.template,
      minScore: record.minScore,
      extension: record.extension,
      templateFileSize: record.templateFileSize,
      maxFileSize: record.maxFileSize! >> 10 << 10,
      fileName: record.fileName,
      storagePath: record.storagePath,
      hash: record.hash,
      uri: record.uri,
      isEnabled: record.isEnabled,
    },
    tmplReady: record !== emptyDocTypeDto,
    tmplName: null as string|null,
    mSizeInKb: (record.maxFileSize! >> 10),
    errors: {} as any,
    touched: {} as any,
  })
  const [states, setStates] = useState(() => initialVal(record))
  useEffect(() => setStates(initialVal(record)), [record])
  const { validate, fieldProps } = usePlainStateFormUtils(states, setStates, topSchema)
  const { registerAssembleFn } = useSubdataAssembleContext<{d:DocumentTemplateInput}>()
  const { assemble, contextValue } = useSubdataAssembleController<DocumentTemplateInput>({ 
    getInformed: (key, value) => {
      // will not get informed
    }
  })
  useEffect(() => {
    return registerAssembleFn('d', async () => {
      const v1 = await waitWith<typeof states>((resolve) =>
        setStates(v => {
          const errors = validate(v.stage) ?? {}
          let ks = Object.keys(v.stage)
          const v1 = {...v, errors, touched: ks.reduce((acc, k) => ({...acc, [k]:true}), {} as any)}
          resolve(v1)
          return v1
        })
      )
      const ks = Object.keys(v1.errors)
      if (ks.length > 0) {
        return new AssembleIssue('')
      }
      const result = await assemble()
      if (result instanceof AssembleIssue) {
        return result
      } 
      const payload : DocumentTemplateInput = {...result, ...v1.stage} as any
      return payload
    })
  }, [assemble, registerAssembleFn, validate])
  
  const { enqueueSnackbar } = useSnackbar()
  const uploadHandlers = useDefaultSavedHandlers('Upload Template File')
  const dispatch = useDispatch()
  const downloadTemplate = (id: any, fileName: any) => {
    dispatch(merge(
      crudGetList('secure-files-doc-tmpl-download', pages(), sorts(), { id: id || '', fileName }),
      {
        onFailure: (err: any) => {
          const errMsg = retrieveAxiosErr(err)
          enqueueSnackbar(`Download failed: ${errMsg}`, { variant: 'error' })
        }
      },
    ))
  }
  const uploadFnRef = useRef((record: any, callbacks?: any) => {})
  const { getRootProps, getInputProps } = useDropzone({
    multiple: false,
    maxFiles: 1,
    maxSize: 10485760, // 10M
    noDrag: true,
    onDropRejected(fileRejections, event) {
      enqueueSnackbar('Unable to upload template file bigger than 10MB', { variant: 'warning' })
    },
    onDropAccepted(files: File[], event: DropEvent) {
      const file = files.pop()
      if (!file) { return }
      const fileSize = file.size
      const filename = file.name
      const iDot = filename.lastIndexOf('.')
      if (iDot === -1) {
        enqueueSnackbar('A template file without file extension is not supported', { variant: 'warning' })
        return
      }
      const extension = filename.substring(iDot)
      if (fileSize === 0) {
        enqueueSnackbar('Empty file can not be used as a template, please choose another file', { variant: 'warning' })
        return
      }
      const input = { file, id: states.stage.recordId }
      uploadFnRef.current(input, {
        onSuccess: ({data}: {data:any}) => {
          const tmplContent: ExtractDocumentResponseDto = data
          if (!tmplContent) {
            enqueueSnackbar('OnData service cannot process this template file. Please make sure the uploaded file is a text-based document instead of an image.', { variant: 'warning' })
            return
          }
          setStates(v => {
            v.stage.template = tmplContent.text
            v.stage.fileName = tmplContent.fileName
            v.stage.storagePath = tmplContent.storagePath
            v.stage.hash = tmplContent.hash
            v.stage.templateFileSize = fileSize
            v.stage.extension = extension
            v.stage.uri = ''
            v.tmplName = filename
            if (v.stage.templateFileSize > v.stage.maxFileSize) {
              v.stage.maxFileSize = v.stage.templateFileSize
              v.mSizeInKb = v.stage.maxFileSize >> 10
            }
            delete v.errors.template
            return {...v, tmplReady: true}
          })
        }
      })
    },
  })
  const tmplDirty = (
    states.prevData.template !== states.stage.template ||
    states.prevData.minScore !== states.stage.minScore ||
    states.prevData.maxFileSize !== states.stage.maxFileSize)

  return (
    <Card>
      <CardHeader title={title} />
      <SubdataAssembleContextProvider value={contextValue as any}>
        <CardContent>
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <FormGroup sx={{ mb: 1 }}>
                <FormControlLabel 
                  label="Enable Document Type" 
                  control={
                    <Switch checked={states.stage.isEnabled} 
                      onChange={(e, checked) => setStates(v => ({...v, stage: {...v.stage, isEnabled: checked},
                        touched: {...v.touched, isEnabled: true },
                      }))} />
                  } 
                />
              </FormGroup>
            </Grid>
            <Grid item xs={6}>
              <TextField label="Name*" disabled={!createMode} fullWidth multiline {...fieldProps('name')}/>
            </Grid>
            <Grid item xs={6}>
              <TextField label="Display Name*" fullWidth multiline {...fieldProps('displayName')}/>
            </Grid>
            <Grid item xs={12}>
              <TextField label="Description" fullWidth multiline {...fieldProps('description')}/>
            </Grid>
            <Grid item xs={12}>
              <Box
                sx={EditorLineSx}
              >
                <Stack direction="row" alignItems="center" spacing={4} marginBottom={1}>
                  <Typography variant="subtitle2">Template</Typography>
                    <CreateOrEdit resource="secure-files-doc-tmpl-upload" basePath="" onFailure={uploadHandlers.onFailure}>
                      <WithSave 
                        render={({save, saving: uploading}) => {
                          uploadFnRef.current = save
                          return (
                          <Tooltip title="Click on this button to upload a template file. Max template file size cannot exceed 10MB.">
                            <IconButton color="primary"
                              arial-label="upload template file"
                              {...(getRootProps() as any)}
                            >
                              <input {...getInputProps()} />
                              {uploading ? 
                                <CircularProgress size="small" sx={{ width: 24 }} /> : 
                                <FileUploadIcon />}
                            </IconButton>
                          </Tooltip>
                          )}}
                      />
                    </CreateOrEdit>
                    {!createMode && states.stage.fileName &&
                      <Tooltip title="Download Template File">
                        <Button onClick={() => downloadTemplate(states.stage.recordId, states.stage.fileName)} sx={{ ml: 3 }}>{states.stage.fileName}</Button>
                      </Tooltip>}
                  {states.touched.template && states.errors.template &&
                    <FormHelperText error>{states.errors.template}</FormHelperText>}
                </Stack>
                <List disablePadding>
                  <ListItem className={tmplDirty ? 'dirty' : undefined}>
                    <FormControl>
                      <FormLabel>Template Info</FormLabel>
                      <Stack direction="row" spacing={2} alignItems="baseline" marginTop={1}>
                        <FormControlLabel label={<Typography variant="caption">Template Ready</Typography>} disabled
                          control={
                            <Checkbox checked={states.tmplReady} />}
                        />
                        <TextField variant="standard" size="small" sx={{ width: 100}} InputLabelProps={{shrink: true}} disabled InputProps={{readOnly: true,}}
                          label={
                            <Stack direction="row" alignItems="start">
                              File Extension
                              <Tooltip title="A template will only be used to match files with the same file extension.">
                                <InfoIcon  sx={{ ml: 2, width: 16, cursor: 'pointer' }}/>
                              </Tooltip>
                            </Stack>} 
                          value={states.stage.extension} 
                          helperText={states.tmplName}
                        />
                        {!!states.stage.templateFileSize && ((()=>{
                          let size = states.stage.templateFileSize >> 10
                          size = size < 1 ? 1 : size
                          return (
                            <TextField label="Template File Size" variant="standard" size="small" sx={{ width: 120}} InputLabelProps={{shrink: true}} disabled type="number"
                              value={size} 
                              InputProps={{
                                readOnly: true,
                                endAdornment: <InputAdornment position="end">KB</InputAdornment>
                              }}
                            />
                          )
                        })())}
                      </Stack>
                    </FormControl>
                    <FormControl sx={{ ml: 8, alignSelf: 'start'}}>
                      <FormLabel sx={{ display: 'flex', flexDirection: 'row', alignItems: 'start'}}>
                        <span>Matching Criteria</span>
                        <Tooltip title="In order to be classified as this Document Type, a file must meet the minimum Matching Threshold and cannot be larger than the Max File Size.">
                          <InfoIcon  sx={{ ml: 2, width: 16, cursor: 'pointer' }}/>
                        </Tooltip>
                      </FormLabel>
                      <Stack direction="row" spacing={3} alignItems="baseline" marginTop={1}>
                        <TextField variant="standard" size="small" sx={{ width: 140}} InputLabelProps={{shrink: true}} type="number" 
                          label={
                            <Stack direction="row" alignItems="start">
                              Matching Threshold
                              <Tooltip title="The Matching Threshold can have a value between 0 and 1. A matching score is automatically calculated by matching a file against the template file. The matching score should at least meet the Matching Threshold in order for this file to be categorized as this Document Type.">
                                <InfoIcon  sx={{ ml: 2, width: 16, cursor: 'pointer' }}/>
                              </Tooltip>
                            </Stack>} 
                          {...fieldProps('minScore', (x:any) => {
                            let n = +x
                            if (Number.isNaN(n)) { return 0.9 }
                            n = parseFloat(n.toFixed(2))
                            return (
                                n < 0.3 ? 0.3
                              : n > 1 ? 1 
                              : n)
                          })}
                          inputProps={{ step: 0.01, max: 1, min: 0, }}
                        />
                        <TextField label="Max File Size" variant="standard" size="small" sx={{ width: 120}} InputLabelProps={{shrink: true}} type="number"
                          InputProps={{
                            endAdornment: <InputAdornment position="end">KB</InputAdornment>
                          }}
                          value={states.mSizeInKb}
                          onChange={(e) => {
                            let n = +e.target.value
                            n = Number.isNaN(n) ? 1 : Math.floor(n)
                            const minVal = (states.stage.templateFileSize ?? 1024) >> 10
                            n = n < minVal ? minVal : n
                            setStates(v => {
                              v.stage.maxFileSize = n << 10
                              return {...v, mSizeInKb: n}
                            })
                          }}
                        />
                      </Stack>
                    </FormControl>
                  </ListItem>
                </List>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <KeywordRuleEditor prevData={states.prevData.keywordRules!} />
            </Grid>
          </Grid>
        </CardContent>
      </SubdataAssembleContextProvider>
      {!createMode &&
        <Backdrop open={states.tmplReady && !states.prevData.template} sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
          <CircularProgress />
        </Backdrop>}
    </Card>
  )
}

export default DocumentTypeEditor;

const emptyDocTypeDto = Object.freeze({
  name: '',
  displayName: '',
  description: '',
  keywordRules: Object.freeze([]) as any,
  template: '',
  minScore: 0.9,
  extension: '',
  templateFileSize: 0,
  maxFileSize: 20971520, // 20M
  isEnabled: true,
} as DocumentTemplateResponseDto)

const KeywordRuleEditor : FC<{ prevData: DocumentKeywordRule[] }> = ({
  prevData: prevKwList,
}) => {
  const initialVal = (prevKwList: DocumentKeywordRule[]) => {
    const isRegex = prevKwList.length > 0 && !!prevKwList[0].regex
    const prevData: KeywordVm = {
      isRegex,
      ls: isRegex ? prevKwList.map(x => x.regex).filter(x => !!x) : (prevKwList[0]?.words ?? []),
    } as any
    return ({
      prevData,
      stage: { 
        vm: prevData.ls.length > 0 ? {...prevData} : null as KeywordVm|null,
        isRemoved: false,
      },
      touched: false,
      error: null as string|null,
    })
  }
  const [states, setStates] = useState(initialVal(prevKwList))
  useEffect(() => { setStates(v => initialVal(prevKwList)) }, [prevKwList])
  const { registerAssembleFn } = useSubdataAssembleContext<DocumentTemplateInput>()
  
  useEffect(() => {
    return registerAssembleFn('keywordRules', async () => {
      const q = promiseWithResolvers<typeof states>()
      setStates(v => { q.resolve(v); return v })
      const v1 = await q.promise
      if (v1.error) {
        return new AssembleIssue('keywordRules')
      }
      if (v1.stage.isRemoved || v1.stage.vm == null) { return [] }
      return (v1.stage.vm.isRegex
        ? v1.stage.vm.ls.map(s => ({ regex: s }))
        : [{ words: v1.stage.vm.ls}])
    })
  }, [registerAssembleFn])
  const actionBtn = () => {
    const isRestore = states.stage.isRemoved
    const actionFn = () => setStates(v => {
      if (isRestore) {
        v.stage.isRemoved = false
      } else if (v.prevData.ls.length === 0) {
        v.stage.vm = null
      } else {
        v.stage.isRemoved = true
      }
      return {...v}
    })
    
    return (
      <Tooltip title={isRestore ? "Restore" : "Remove"}>
        <IconButton color="secondary" onClick={actionFn}>
          {isRestore ? <ReplayIcon /> : <ClearIcon />}
        </IconButton>
      </Tooltip>)
  }
  const classOf = (d: KeywordVm) => {
    return (
      states.stage.isRemoved ? 'removed' 
      : states.prevData.ls.length === 0 ? 'added' 
      : (states.prevData.isRegex !== d.isRegex
        || states.prevData.ls.length !== d.ls.length
        || states.prevData.ls.some(s => d.ls.indexOf(s) === -1)) ? 'dirty'
      : undefined
    )
  }
  const radioHandleChange = (e:any, value:string) => {
    const isRegex = value === 'regex'
    setStates(v => {
      const vm = v.stage.vm!
      if (vm.isRegex === isRegex) { return v }
      vm.isRegex = isRegex
      if (isRegex) {
        vm.ls = vm.ls.filter(isValidRegex)
      }
      return {...v}
    })
  }
  const isValidRegex = (s:string) => {
    try {
      new RegExp(s)
      return true
    } catch {
      return false
    }
  }

  return (
    <Box
      sx={EditorLineSx}
    >
      <Stack direction="row" alignItems="center" spacing={4} marginBottom={1}>
        <Typography variant="subtitle2">Mandatory Keywords</Typography>
        {!states.stage.vm &&
          <Tooltip title="Add">
            <IconButton color="primary"
              onClick={() => setStates(v => {
                return {...v, stage: {...v.stage, vm: {...v.prevData}}}
              })} 
            >
              <AddIcon /></IconButton>
          </Tooltip>}
      </Stack>
      <Alert severity="info" sx={{ mb: 1 }}>
        A file must contain all of keywords or match all regular expressions below before it is evaluated against the template file.
      </Alert>
      <List disablePadding>
        {states.stage.vm &&
          <ListItem className={classOf(states.stage.vm)}>
            <Stack direction="row" flexWrap="wrap" alignItems="center" width="100%" marginBottom={2}>
              <FormControl sx={{ mr: 'auto'}} size="small">
                <FormLabel>Type</FormLabel>
                <RadioGroup row value={states.stage.vm.isRegex ? 'regex' : 'words'} onChange={radioHandleChange}>
                  <FormControlLabel value="words" label="Keywords" control={<Radio />} />
                  <FormControlLabel value="regex" label="Regular Expressions" control={<Radio />} />
                </RadioGroup>
              </FormControl>
              {actionBtn()}
              <Box width="100%" marginBottom={2}>
                <StringArrayTextField list={states.stage.vm.ls ?? []} stackChips="row"    
                  setList={(ls) => setStates(v => {
                    const ls1 = [...new Set(ls)]
                    v.stage.vm!.ls = v.stage.vm!.isRegex ? ls1.filter(isValidRegex) : ls1
                    return ({...v, 
                      touched: true,
                      error: ls1.length === 0 ? 'At least one value is required' : null
                    })
                  })} 
                  acceptValue={(s) => {
                    if (!states.stage.vm!.isRegex) { return true }
                    const a = isValidRegex(s)
                    if (!a) {
                      setTimeout(() =>
                        setStates(v => ({...v, touched: true, error: 'Regular Expression value is incorrect'}))
                      )
                    }
                    return a
                  }}
                  placeholder="Add a new value and press the Enter key to confirm..."
                  error={Boolean(states.touched && states.error)}
                  helperText={states.touched && states.error}
                />
              </Box>
            </Stack>
          </ListItem>}
      </List>
    </Box>
  )
}

interface KeywordVm {
  ls: string [],
  isRegex: boolean,
}

const EditorLineSx : SxProps<Theme> = {
  '& li.MuiListItem-root': {
    pl: 3,
    '&:hover': {
      bgcolor: theme => theme.palette.action.hover,
    },
    '&::before': {
      display: 'block',
      content: "''",
      position: 'absolute',
      left: 0,
      height: 'calc(100% - 2px)',
      width: 4,
      bgcolor: 'divider'
    }
  },
  '& li.MuiListItem-root.added::before': { bgcolor: 'success.light' },
  '& li.MuiListItem-root.removed': {
    '&::before': { bgcolor: 'error.light' },
    '& input, & textarea': { textDecoration: 'line-through' },
  },
  '& li.MuiListItem-root.dirty::before': { bgcolor: 'info.light' },
}


